// Клиентский чанк графа маршрутизации: разбор бинарного формата v1
// (backend/app/routing/graph_chunk.py), смежность (CSR) и пространственный
// индекс. Все расстояния - в метрах Web Mercator (EPSG:3857), как у серверного
// pgRouting-графа (ST_Length/ST_Distance по geom в 3857), чтобы результаты
// клиентского роутера сопоставлялись с серверными один к одному.

export type BBox = [number, number, number, number]; // minLon, minLat, maxLon, maxLat
export type LonLat = [number, number];

export interface GraphObstacle {
  kind: 1 | 2; // 1 - обрыв, 2 - река
  points: Float64Array; // lon, lat попарно
}

export interface GraphChunk {
  lon: Float64Array;
  lat: Float64Array;
  edgeA: Uint32Array;
  edgeB: Uint32Array;
  obstacles: GraphObstacle[];
  bbox: BBox;
}

const R = 6378137;
export const toMercator = (lon: number, lat: number): LonLat => [
  (lon * Math.PI * R) / 180,
  R * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)),
];
export const fromMercator = (x: number, y: number): LonLat => [
  (x / R) * (180 / Math.PI),
  (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI),
];

const HEADER_BYTES = 24;
const littleEndianHost = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

export function decodeGraphChunk(buf: ArrayBuffer, bbox: BBox): GraphChunk {
  if (!littleEndianHost) throw new Error("big-endian host is not supported");
  if (buf.byteLength < HEADER_BYTES) throw new Error("graph chunk truncated");
  const dv = new DataView(buf);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (magic !== "ECRG") throw new Error("graph chunk: bad magic");
  if (dv.getUint32(4, true) !== 1) throw new Error("graph chunk: unsupported version");
  const V = dv.getUint32(8, true);
  const E = dv.getUint32(12, true);
  const O = dv.getUint32(16, true);
  let off = HEADER_BYTES;
  if (buf.byteLength < off + 8 * V + 8 * E) throw new Error("graph chunk truncated");

  const vi = new Int32Array(buf, off, 2 * V);
  const lon = new Float64Array(V);
  const lat = new Float64Array(V);
  for (let i = 0; i < V; i++) {
    lon[i] = vi[2 * i] / 1e7;
    lat[i] = vi[2 * i + 1] / 1e7;
  }
  off += 8 * V;
  const ei = new Uint32Array(buf, off, 2 * E);
  const edgeA = new Uint32Array(E);
  const edgeB = new Uint32Array(E);
  for (let i = 0; i < E; i++) {
    edgeA[i] = ei[2 * i];
    edgeB[i] = ei[2 * i + 1];
  }
  off += 8 * E;

  const obstacles: GraphObstacle[] = [];
  for (let k = 0; k < O; k++) {
    if (buf.byteLength < off + 8) throw new Error("graph chunk truncated");
    const kind = dv.getUint32(off, true) as 1 | 2;
    const n = dv.getUint32(off + 4, true);
    off += 8;
    if (buf.byteLength < off + 8 * n) throw new Error("graph chunk truncated");
    const pi = new Int32Array(buf, off, 2 * n);
    const points = new Float64Array(2 * n);
    for (let i = 0; i < 2 * n; i++) points[i] = pi[i] / 1e7;
    off += 8 * n;
    obstacles.push({ kind, points });
  }
  return { lon, lat, edgeA, edgeB, obstacles, bbox };
}

// Склейка тайлов: вершины - по координате (E7), рёбра на границе тайла лежат в
// обоих тайлах - по паре концов; препятствия (обрезаны по тайлу) просто складываются.
export function mergeChunks(chunks: GraphChunk[]): GraphChunk {
  const vIndex = new Map<string, number>();
  const lon: number[] = [];
  const lat: number[] = [];
  const edgeKeys = new Set<number>();
  const ea: number[] = [];
  const eb: number[] = [];
  const obstacles: GraphObstacle[] = [];
  let bbox: BBox | null = null;
  for (const c of chunks) {
    const remap = new Uint32Array(c.lon.length);
    for (let i = 0; i < c.lon.length; i++) {
      const k = `${Math.round(c.lon[i] * 1e7)},${Math.round(c.lat[i] * 1e7)}`;
      let id = vIndex.get(k);
      if (id === undefined) {
        id = lon.length;
        vIndex.set(k, id);
        lon.push(c.lon[i]);
        lat.push(c.lat[i]);
      }
      remap[i] = id;
    }
    for (let e = 0; e < c.edgeA.length; e++) {
      const a = remap[c.edgeA[e]], b = remap[c.edgeB[e]];
      const k = Math.min(a, b) * 4294967296 + Math.max(a, b);
      if (edgeKeys.has(k)) continue;
      edgeKeys.add(k);
      ea.push(a);
      eb.push(b);
    }
    obstacles.push(...c.obstacles);
    bbox = bbox
      ? [Math.min(bbox[0], c.bbox[0]), Math.min(bbox[1], c.bbox[1]), Math.max(bbox[2], c.bbox[2]), Math.max(bbox[3], c.bbox[3])]
      : c.bbox;
  }
  return {
    lon: Float64Array.from(lon),
    lat: Float64Array.from(lat),
    edgeA: Uint32Array.from(ea),
    edgeB: Uint32Array.from(eb),
    obstacles,
    bbox: bbox ?? [0, 0, 0, 0],
  };
}

const CELL_M = 150;
const key = (cx: number, cy: number) => cx * 100003 + cy;

export interface GraphIndex {
  chunk: GraphChunk;
  mx: Float64Array;
  my: Float64Array;
  adjStart: Uint32Array;
  adjTo: Uint32Array;
  adjEdge: Uint32Array;
  edgeLen: Float64Array; // метры 3857
  grid: {
    minX: number;
    minY: number;
    cols: number;
    rows: number;
    vertexCells: Map<number, number[]>;
    edgeCells: Map<number, number[]>;
  };
}

export function buildGraphIndex(chunk: GraphChunk): GraphIndex {
  const V = chunk.lon.length;
  const E = chunk.edgeA.length;
  const mx = new Float64Array(V);
  const my = new Float64Array(V);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < V; i++) {
    const [x, y] = toMercator(chunk.lon[i], chunk.lat[i]);
    mx[i] = x;
    my[i] = y;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (V === 0) minX = minY = maxX = maxY = 0;

  const deg = new Uint32Array(V + 1);
  const edgeLen = new Float64Array(E);
  for (let e = 0; e < E; e++) {
    const a = chunk.edgeA[e], b = chunk.edgeB[e];
    edgeLen[e] = Math.hypot(mx[b] - mx[a], my[b] - my[a]);
    deg[a + 1]++;
    deg[b + 1]++;
  }
  for (let i = 0; i < V; i++) deg[i + 1] += deg[i];
  const adjStart = deg;
  const fill = adjStart.slice(0, V);
  const adjTo = new Uint32Array(2 * E);
  const adjEdge = new Uint32Array(2 * E);
  for (let e = 0; e < E; e++) {
    const a = chunk.edgeA[e], b = chunk.edgeB[e];
    adjTo[fill[a]] = b;
    adjEdge[fill[a]++] = e;
    adjTo[fill[b]] = a;
    adjEdge[fill[b]++] = e;
  }

  const cols = Math.floor((maxX - minX) / CELL_M) + 1;
  const rows = Math.floor((maxY - minY) / CELL_M) + 1;
  const vertexCells = new Map<number, number[]>();
  const edgeCells = new Map<number, number[]>();
  const cellOf = (x: number, y: number): [number, number] => [Math.floor((x - minX) / CELL_M), Math.floor((y - minY) / CELL_M)];
  for (let i = 0; i < V; i++) {
    const [cx, cy] = cellOf(mx[i], my[i]);
    const k = key(cx, cy);
    const list = vertexCells.get(k);
    if (list) list.push(i);
    else vertexCells.set(k, [i]);
  }
  for (let e = 0; e < E; e++) {
    const a = chunk.edgeA[e], b = chunk.edgeB[e];
    const [ax, ay] = cellOf(Math.min(mx[a], mx[b]), Math.min(my[a], my[b]));
    const [bx, by] = cellOf(Math.max(mx[a], mx[b]), Math.max(my[a], my[b]));
    for (let cx = ax; cx <= bx; cx++) {
      for (let cy = ay; cy <= by; cy++) {
        const k = key(cx, cy);
        const list = edgeCells.get(k);
        if (list) list.push(e);
        else edgeCells.set(k, [e]);
      }
    }
  }
  return { chunk, mx, my, adjStart, adjTo, adjEdge, edgeLen, grid: { minX, minY, cols, rows, vertexCells, edgeCells } };
}

export interface NearVertex {
  vertex: number;
  distM: number;
}

// limit ближайших вершин без ограничения по радиусу (как nearest_start_vertices).
export function nearestVertices(index: GraphIndex, lon: number, lat: number, limit: number): NearVertex[] {
  const { grid, mx, my } = index;
  if (!mx.length) return [];
  const [px, py] = toMercator(lon, lat);
  const cx = Math.floor((px - grid.minX) / CELL_M);
  const cy = Math.floor((py - grid.minY) / CELL_M);
  const bound = Math.max(grid.cols, grid.rows) + 2 + Math.max(Math.abs(cx), Math.abs(cy), Math.abs(cx - grid.cols), Math.abs(cy - grid.rows));
  const found: NearVertex[] = [];
  for (let r = 0; r <= bound; r++) {
    for (let x = cx - r; x <= cx + r; x++) {
      for (let y = cy - r; y <= cy + r; y++) {
        if (Math.max(Math.abs(x - cx), Math.abs(y - cy)) !== r) continue; // только кольцо r
        const list = grid.vertexCells.get(key(x, y));
        if (!list) continue;
        for (const v of list) found.push({ vertex: v, distM: Math.hypot(mx[v] - px, my[v] - py) });
      }
    }
    if (found.length >= limit) {
      found.sort((a, b) => a.distM - b.distM);
      if (found[limit - 1].distM <= r * CELL_M) break;
    }
  }
  found.sort((a, b) => a.distM - b.distM);
  return found.slice(0, limit);
}

export interface NearEdge {
  edge: number;
  distM: number;
  fraction: number; // вдоль ребра от edgeA к edgeB
  point: LonLat; // ближайшая точка на ребре
}

// Рёбра в радиусе radiusM (метры 3857, как ST_DWithin), ближайшие первыми.
export function nearestEdges(index: GraphIndex, lon: number, lat: number, radiusM: number, limit: number): NearEdge[] {
  const { grid, mx, my, chunk } = index;
  if (!chunk.edgeA.length) return [];
  const [px, py] = toMercator(lon, lat);
  const cx = Math.floor((px - grid.minX) / CELL_M);
  const cy = Math.floor((py - grid.minY) / CELL_M);
  const k = Math.ceil(radiusM / CELL_M);
  const seen = new Set<number>();
  const out: NearEdge[] = [];
  for (let x = cx - k; x <= cx + k; x++) {
    for (let y = cy - k; y <= cy + k; y++) {
      const list = grid.edgeCells.get(key(x, y));
      if (!list) continue;
      for (const e of list) {
        if (seen.has(e)) continue;
        seen.add(e);
        const a = chunk.edgeA[e], b = chunk.edgeB[e];
        const dx = mx[b] - mx[a], dy = my[b] - my[a];
        const len2 = dx * dx + dy * dy;
        const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - mx[a]) * dx + (py - my[a]) * dy) / len2)) : 0;
        const qx = mx[a] + t * dx, qy = my[a] + t * dy;
        const d = Math.hypot(px - qx, py - qy);
        if (d <= radiusM) out.push({ edge: e, distM: d, fraction: t, point: fromMercator(qx, qy) });
      }
    }
  }
  out.sort((a, b) => a.distM - b.distM);
  return out.slice(0, limit);
}
